import React, { useMemo, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Button } from '@/components/ui/button';
import { Check, ChevronsUpDown } from 'lucide-react';

export const CustomerCombobox = ({ customers, value, onChange, placeholder = 'Velg kunde...', testId = 'customer-combobox', excludeIds = [] }) => {
  const [open, setOpen] = useState(false);
  const selected = customers.find((c) => c.id === value);
  const excludeKey = excludeIds.join(',');
  const options = useMemo(() => customers
    .filter((c) => !excludeKey.split(',').includes(c.id))
    .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'nb'))
    .map((c) => ({
      c,
      searchValue: `${c.name} ${c.address || ''} ${c.postal_code || ''} ${c.region || ''} ${c.contact_person || ''} ${c.id}`,
      subtitle: [c.address, c.postal_code, c.region].filter(Boolean).join(' · '),
    })), [customers, excludeKey]);

  return (
    <Popover open={open} onOpenChange={setOpen} modal>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" role="combobox" aria-expanded={open} className="w-full justify-between font-normal" data-testid={testId}>
          <span className={`truncate ${selected ? 'text-slate-900' : 'text-slate-500'}`}>{selected ? selected.name : placeholder}</span>
          <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)] min-w-[260px]" align="start">
        <Command>
          <CommandInput placeholder="Søk navn, adresse, postnr, område..." data-testid={`${testId}-search`} />
          <CommandList className="max-h-64">
            <CommandEmpty>Ingen kunder funnet</CommandEmpty>
            <CommandGroup>
              {options.map(({ c, searchValue, subtitle }) => (
                <CommandItem
                  key={c.id}
                  value={searchValue}
                  onSelect={() => { onChange(c.id); setOpen(false); }}
                  data-testid={`${testId}-option-${c.id}`}
                >
                  <Check className={`h-4 w-4 ${value === c.id ? 'opacity-100' : 'opacity-0'}`} />
                  <div className="min-w-0">
                    <div className="truncate font-medium">{c.name}</div>
                    {subtitle && <div className="truncate text-xs text-slate-400">{subtitle}</div>}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default CustomerCombobox;
